import { Navigate, RouteObject } from "react-router";

import { AuthenticationGuard } from "@/modules/auth/components/authentication-guard";
import { LandingRedirect } from "@/modules/layout/components/landing-redirect";
import { MainLayout } from "@/modules/layout/components/main-layout";
import { routePaths } from "@/router/route-paths";

import { lazyRoute } from "./lazy-route";
import { RequireSection, type Section } from "./require-section";

const guardedLazyRoute =
  (
    section: Section,
    getImport: () => Promise<{ default: React.ComponentType }>,
  ) =>
  async () => {
    const { default: Component } = await getImport();
    return {
      Component: () => (
        <RequireSection section={section}>
          <Component />
        </RequireSection>
      ),
    };
  };

const Screens = {
  Procedures: () => import("@/modules/procedures/screens/procedures-screen"),
  NewProcedure: () =>
    import("@/modules/procedures/screens/new-procedure-screen"),
  EditProcedure: () =>
    import("@/modules/procedures/screens/edit-procedure-screen"),
  ProcedureDrawer: () =>
    import("@/modules/procedures/screens/procedure-drawer-screen"),
  Reports: () => import("@/modules/reports/screens/reports-screen"),
  ReportRedirect: () =>
    import("@/modules/reports/screens/report-redirect-screen"),
  Signatures: () => import("@/modules/signatures/screens/signatures-screen"),
  NewSignature: () =>
    import("@/modules/signatures/screens/signature-create-screen"),
  EditSignature: () =>
    import("@/modules/signatures/screens/signature-edit-screen"),
};

export const authenticatedRoutes: RouteObject[] = [
  {
    element: <AuthenticationGuard component={MainLayout} />,
    children: [
      {
        path: routePaths.base,
        element: <LandingRedirect />,
      },
      {
        path: routePaths.procedures,
        lazy: guardedLazyRoute("procedures", Screens.Procedures),
        children: [
          {
            path: routePaths.procedureDetailDrawer,
            lazy: lazyRoute(Screens.ProcedureDrawer),
          },
        ],
      },
      {
        path: routePaths.procedureViewsIndex,
        element: <Navigate to={routePaths.proceduresLanding} replace />,
      },
      {
        // The view route is not a new screen — it is the same procedures screen
        // with a slug in scope. The drawer nests under it so the view's filtered
        // list stays mounted behind the drawer and closing is a relative `..`.
        path: routePaths.procedureViews,
        lazy: guardedLazyRoute("procedures", Screens.Procedures),
        children: [
          {
            path: routePaths.procedureViewDrawer,
            lazy: lazyRoute(Screens.ProcedureDrawer),
          },
        ],
      },
      {
        path: routePaths.procedureEdit,
        lazy: guardedLazyRoute("procedures", Screens.EditProcedure),
      },
      {
        path: routePaths.newProcedure,
        lazy: guardedLazyRoute("procedures", Screens.NewProcedure),
      },
      {
        path: routePaths.reports,
        lazy: guardedLazyRoute("reports", Screens.Reports),
      },
      {
        path: routePaths.report,
        lazy: guardedLazyRoute("reports", Screens.ReportRedirect),
      },
      {
        path: routePaths.signatures,
        lazy: guardedLazyRoute("signatures", Screens.Signatures),
      },
      {
        path: routePaths.newSignature,
        lazy: guardedLazyRoute("signatures", Screens.NewSignature),
      },
      {
        path: routePaths.editSignature,
        lazy: guardedLazyRoute("signatures", Screens.EditSignature),
      },
    ],
  },
];
